import { Controller } from "@hotwired/stimulus"
import { get } from "@rails/request.js"
import * as maplibregl from "maplibre-gl"
import maplibreWorkerUrl from "maplibre-gl/dist/maplibre-gl-worker.mjs?worker&url"

import { normaliseViewport, unwrapBox } from "./map_bounds"
import { drawnFeatures, pluralise } from "./map_boxes"
import { parseHex, pinImage } from "./map_pins"

// Same worker resolution as controllers/maps/route_controller.js — maplibre-gl
// v6 is ESM-only and cannot locate its own worker under a bundler, and
// `?worker&url` (not plain `?url`) emits a self-contained chunk that keeps the
// dist worker's sibling `maplibre-gl-shared.mjs` in a production build. Calling
// setWorkerUrl twice with the same URL is a no-op, so both controllers declare it.
maplibregl.setWorkerUrl(maplibreWorkerUrl)

// The only scheme ever assigned to the popover's href. The value comes from a
// third-party source or a form the rep can edit; a `javascript:` URL here would
// run for whoever clicks the link, so anything else hides the row instead.
const HTTP_URL = /^https?:\/\//i
// The only hrefs the card builds from a phone or an email, both made
// server-side (Sources::Result.tel_url / .mailto_url) and checked again here,
// as the website is: digits behind `tel:`, and an address with no `?`, `&`,
// `%` or `#` — so no header or second recipient — behind `mailto:`.
const TEL_URL = /^tel:\+?\d{3,}$/
const MAILTO_URL = /^mailto:[^\s?&%#@]+@[^\s?&%#@]+$/
// How many executives the card names; the server counts the rest.
const CARD_EXECUTIVES = 3

// Last-resort pin colour, for the impossible case of a feature arriving before
// the palette does. The palette itself is the SERVER's — Prospecting::MapFeature
// ::PIN_COLOURS, handed over as `pinColorsValue` — because the same five colours
// are drawn in three places (this canvas, the dot on each list row, and the
// legend that decodes them) and when each place owned its own copy they drifted:
// the view reached for Tailwind tokens while the canvas painted hexes, so
// the legend told a rep a dot meant something else. The pins' one
// body colour is the server's too (MapFeature::PIN_BODY_COLOUR, as
// `pinBodyColorValue`); black stands in only when a page forgot to send it.
const FALLBACK_COLOR = "#a1a1aa"
const FALLBACK_BODY = [0, 0, 0]

// Each tone's pin is an image registered on the map under this prefix and
// the tone; a tone missing from the palette gets the bare prefix, drawn in
// FALLBACK_COLOR.
const PIN_IMAGE_PREFIX = "prospecting-pin"

// Draw order within the one pin layer, highest on top. A new candidate is
// the only pin on this map the rep can still act on, so it is never the one
// hidden underneath: where a discovery sits on a building the team already
// saved or already sells to, the candidate's dot is the one that takes the click.
// The list is ordered the same way, so a row and its pin agree about which of
// two stacked pins is the readable one.
//
// `symbol-sort-key` and not source order: MapLibre's feature order within a
// layer is an implementation detail, and this is a promise about what a rep
// sees.
const KIND_SORT_KEYS = {
  saved: 1,
  known: 2,
}
const CANDIDATE_SORT_KEY = 3
// The pin the rep is inspecting goes above all of them: a saved site linked
// to an account stands on that account's own pin, and the selected one must
// not be drawn under the pin it was chosen over. Above every resting key
// (the largest is CANDIDATE_SORT_KEY + 10).
const SELECTED_SORT_KEY = 100

// Pin sizes, as `icon-size` over the resting pin (./map_pins): the resting
// size and the one the rep is inspecting. A CONTEXT pin — a lead, prospect,
// customer or saved site shown alongside a search's results (the server marks
// those `dimmed` — see the searches view) — is drawn exactly as it was before
// the search: it only sinks below the results (#_featureCollection). It used
// to shrink, then to fade, and both read as the map losing the pins the rep
// already knew (the user's call, 2026-10-13).
const BASE_SIZE = 1
const SELECT_SIZE = 1.625

// The search form's fields that search again as they are picked (#refine).
const REFINING_FIELDS = ["group_id", "industry", "not_visited"]

const SOURCE_ID = "prospecting-sites"
const LAYER_ID = "prospecting-sites-pins"

// The route layer: plan mode's route, drawn apart from the sites so that no
// "Also show on the map" box and no dimming ever reaches it. One line per
// route day and a numbered pin per stop, each in its day's colour.
const ROUTE_SOURCE_ID = "prospecting-route-lines"
const ROUTE_CASING_ID = "prospecting-route-lines-casing"
const ROUTE_LINE_ID = "prospecting-route-lines-line"

// Copied from routing's route map (controllers/maps/route_controller.js), not
// called: Prospecting does not depend on routing. Its round joins and caps,
// under the demo's white casing so a day's line reads on either basemap.
// Plain lines, never a filled area.
const ROUTE_LINE_LAYOUT = { "line-join": "round", "line-cap": "round" }
const ROUTE_CASING_PAINT = {
  "line-color": "#ffffff",
  "line-width": 8,
  "line-opacity": 0.9,
}
const ROUTE_LINE_PAINT = { "line-color": ["get", "colour"], "line-width": 4.5 }

// The selected account's other sites: a dashed line from its main location
// to each one, and a dot where each stands. Drawn apart from the pins, under
// them, so no box and no dimming reaches it; cleared with the selection.
const ACCOUNT_SITES_SOURCE_ID = "prospecting-account-sites"
const ACCOUNT_SITES_LINE_ID = "prospecting-account-sites-lines"
const ACCOUNT_SITES_DOT_ID = "prospecting-account-sites-dots"
// The account id's place in the server's URL template (accountSitesUrl).
const ACCOUNT_ID_PLACEHOLDER = "__ACCOUNT_ID__"

// Lead Finder discovery map.
//
// The map IS the page here: pins for every result and every saved site, a list
// on the left, a detail card on the right. A row and its pin are the same
// object — both route through `select(id)` — so the two never disagree about
// what is selected.
export default class extends Controller {
  static values = {
    styleUrl: String,
    // The aerial basemap the "Satellite" toggle swaps in. Optional: when the
    // environment ships no key there is no second descriptor either, and the
    // toggle stays hidden rather than swapping the map to a blank tile grid.
    satelliteStyleUrl: String,
    apiKey: String,
    attribution: String,
    // The picked place's box, {south, west, north, east}, or {} for none. When
    // it is set it WINS over fitting to the pins: a rep who searched Madrid has
    // to land on Madrid even when nothing was found there, rather than staying
    // on the previous viewport and reading an unchanged map as "no results
    // anywhere". The server omits it when the rep ticked "search this area
    // only" — the map is then already where it belongs.
    placeBounds: Object,
    // Where the map was standing when this search was submitted, {south, west,
    // north, east} or {} on a first visit. The hidden bounds fields post it on
    // every search whether or not the rep ticked "search this area only", so
    // this is always the previous viewport and never a filter. It is what makes
    // a search STAY PUT: a search is a change to the list, not a reason to move
    // the camera, and the old fit-to-every-pin zoomed out to whatever continent
    // the furthest result happened to be on. Only a picked place outranks it.
    viewportBounds: Object,
    // { candidate:, lead:, prospect:, customer:, saved: } — keyed by a feature's
    // TONE, which is not its `kind`: an account we already have is painted by
    // where it sits in the funnel, not by being an account. See FALLBACK_COLOR
    // above for why the server owns this rather than each renderer keeping its
    // own copy.
    pinColors: Object,
    // The one colour every pin's body is drawn in; the tone is its dot.
    pinBodyColor: String,
    features: Array,
    // The box of the place the server parsed out of the query ("madrid" out
    // of "coca cola madrid"). When all four edges are finite it WINS the
    // initial fit over the pins: the rep named an area, so the map shows them
    // where the search went rather than zooming to whatever pins exist.
    locationBox: Object,
    // The context tones whose "Also show on the map" boxes are ticked. A
    // feature flagged `context` is drawn only while its tone is here; the
    // boxes edit it (#toggleTone) and nothing is asked of the server.
    shownTones: Array,
    // Whether the page holds the CRM's Sites in the area. False before any
    // search has run: an account box ticked then searches again (#toggleTone).
    contextLoaded: Boolean,
    // Where an account's sites are read, with ACCOUNT_ID_PLACEHOLDER for its
    // id. Rendered only for a rep who may read the CRM's Sites; without it a
    // selection draws no lines.
    accountSitesUrl: String,
    // The feature a Site chosen from the Company box's dropdown landed on:
    // the map opens on it, selected, instead of on the previous viewport.
    focusedId: String,
  }

  static targets = [
    "canvas",
    "row",
    "south",
    "west",
    "north",
    "east",
    "detail",
    "detailName",
    "detailAddress",
    "detailKnownAccount",
    "detailStatus",
    "detailStatusRow",
    "detailEmployees",
    "detailEmployeesRow",
    "detailRevenue",
    "detailRevenueRow",
    "detailSector",
    "detailSectorRow",
    "detailSource",
    "detailWebsite",
    "detailWebsiteRow",
    "detailPhone",
    "detailPhoneRow",
    "detailEmail",
    "detailEmailRow",
    "detailParent",
    "detailParentCountry",
    "detailParentRow",
    "detailExecutives",
    "detailExecutivesMore",
    "detailExecutivesRow",
    "executiveTemplate",
    "detailNoLocation",
    "saveForm",
    "saveFeatureId",
    "saveError",
    "candidateField",
    "openLink",
    // "Create qualified lead" on a saved pin's card, and the frame its form
    // opens in (sites/_convert_form).
    "convertFrame",
    "convertTemplate",
    "convertLink",
    // What a card's save or conversion answered (searches/_site_marker).
    "siteMarker",
    "accountLink",
    // "See all locations" on a Site's card, and the line it says when the
    // account has no other location to draw.
    "accountSitesRow",
    "accountSitesButton",
    "accountSitesNone",
    "accountSiteForm",
    "accountSiteField",
    "basemapButton",
    "basemapSwitch",
    // What the boxes redraw in the list beside the map.
    "listItem",
    "groupHeading",
    "emptyState",
    "count",
    "unmappedNote",
    "cappedNote",
    "knownTruncatedNote",
    "legend",
    "legendList",
    "legendTone",
    // The form's record of the boxes, kept in step with a click.
    "boxesDefault",
    "boxesChosen",
    // The search form, submitted again by an account box on a page that
    // never read the CRM's Sites.
    "searchForm",
  ]

  connect() {
    // EVERY feature is indexed, including the ones with no coordinate: they are
    // rows in the list and must open a detail card like any other. Only the
    // locatable subset reaches the map.
    const all = this._withCoords(this.featuresValue)
    this._byId = new Map(all.map((f) => [f.id, f]))
    this._selectedId = null
    this._basemap = "map"
    this._features = null
    this._route = this._route || null
    this._stopPins = []
    // Stimulus connects targets BEFORE it calls connect(), so a marker that is
    // already filled — Back to a cached page after a card's save — reached
    // siteMarkerTargetConnected with no index to look its pin up in. Played
    // again now the index exists; an empty marker is a no-op.
    this.siteMarkerTargets.forEach((marker) =>
      this.siteMarkerTargetConnected(marker),
    )

    // No key configured for this environment: the list still works, the map
    // just stays an empty panel. Same contract as maps--route.
    if (!this.apiKeyValue) return
    // No canvas means this page decided there is nothing to mount into.
    // Falling back to `this.element` would paint a full-width globe across the
    // whole layout, which is exactly what it used to do.
    if (!this.hasCanvasTarget) return

    const apiKey = this.apiKeyValue
    // Every locatable feature goes to the map's source, drawn or not, so a
    // box can draw it later; the camera only cares about what is drawn now.
    const features = all.filter((f) => f.lngLat)
    const drawn = this._drawn(features)

    // Camera precedence, decided ONCE and applied as one move: the picked place
    // wins (a rep who asked for Madrid lands on Madrid even when nothing was
    // found there), then the viewport the search was submitted from (the map
    // stays exactly where the rep left it), and only a first visit with neither
    // falls back to fitting the pins. Fitting on every search is what made the
    // map "pan out again" each time.
    // A Site chosen from the dropdown outranks both: the rep asked for that
    // one building, so the map fits it (and, once selected, its other sites).
    const focus = this.focusedIdValue
      ? (this._byId.get(this.focusedIdValue) ?? null)
      : null
    const focused = focus && focus.lngLat ? focus : null
    const placeBox = focused ? null : this._placeBox()
    const restoreBox = placeBox || focused ? null : this._viewportBox()
    // Where the camera SITS before the fit below runs, so the first painted
    // frame is not a different place than the second. A restored viewport is
    // seeded from its own centre: seeding from the first pin there would show
    // the rep one continent and then slide them to another, which is the jump
    // this whole path exists to remove.
    const seed = this._boxCentre(restoreBox)
    const seedCentre = seed ?? (drawn.length > 0 ? drawn[0].lngLat : [0, 0])

    // Turbo restores a cached snapshot of this page with the PREVIOUS map's
    // DOM still inside the canvas, and MapLibre mounting into it stacks a
    // second canvas on the first. The container is ours to empty.
    this.canvasTarget.replaceChildren()
    this._map = new maplibregl.Map({
      container: this.canvasTarget,
      style: this.styleUrlValue,
      // [0,0] — the last fallback above — is the Gulf of Guinea, so world zoom
      // is reached only when there is nothing at all to look at.
      center: seedCentre,
      zoom: seed !== null || drawn.length > 0 ? 10 : 2,
      attributionControl: false,
      transformRequest: (url) => {
        if (url.includes("amazonaws.com")) {
          const separator = url.includes("?") ? "&" : "?"
          return { url: `${url}${separator}key=${apiKey}` }
        }
        return { url }
      },
    })

    // AWS/HERE require attribution to be present (§82 AWS Service Terms), so it
    // cannot be dropped; compact collapses it to an "ⓘ" that expands on tap.
    this._map.addControl(
      new maplibregl.AttributionControl({
        compact: true,
        customAttribution: this.attributionValue || "© HERE 2026",
      }),
      "bottom-right",
    )
    this._map.addControl(new maplibregl.NavigationControl(), "bottom-right")

    // AWS's descriptor ships `globe`, a 3-D planet floating in space — a lot of
    // ocean and no useful detail at the zoom a rep works at. Set on style.load
    // because the style overwrites the projection when it lands.
    this._map.on("style.load", () => {
      this._map.setProjection({ type: "mercator" })
      // setStyle() throws away every source, layer and image the app added —
      // the pins are ours, not the descriptor's, so they have to be put back
      // by hand on each swap. No-ops on the first load, which happens before
      // `load` has built them.
      this._restoreSitesLayer()
      // The route's lines too, under the pins just put back. Its numbered
      // pins are DOM markers and never left.
      this._restoreRouteLayer()
      // And the selected account's lines to its other sites.
      this._addAccountSitesLayer()
    })

    // Rendered hidden so it never flashes on a page whose map never mounts
    // (no key, no canvas — both return above this line).
    if (this.hasBasemapSwitchTarget && this.satelliteStyleUrlValue) {
      this.basemapSwitchTarget.hidden = false
    }

    this._onMoveEnd = () => this._writeBounds()
    this._map.on("load", () => {
      this._renderFeatures(features, { fit: !placeBox && !restoreBox })
      if (placeBox) this._fitBox(placeBox)
      // padding: 0 because this box came from getBounds(), which reports the
      // WHOLE canvas including the strip under the floating panels. Fitting it
      // back with the panel padding would shrink the view a little on every
      // search — a slow zoom-out spread over a session.
      if (restoreBox) {
        this._fitBox(restoreBox, { padding: 0, maxZoom: null, animate: false })
      }
      this._loaded = true
      this._drawRoute()
      if (focus) this.select(focus.id)
      this._drawAccountSites()
      if (this._pendingFit) this._fitBox(this._pendingFit, { maxZoom: 13 })
      this._pendingFit = null
      this._writeBounds()
    })
    this._map.on("moveend", this._onMoveEnd)
  }

  disconnect() {
    this._clearStopPins()
    if (this._map) {
      this._map.off("moveend", this._onMoveEnd)
      this._map.remove()
      this._map = null
    }
    this._loaded = false
    this._pendingFit = null
    this._accountSites = null
    this._accountSitesRequest = (this._accountSitesRequest || 0) + 1
  }

  // ── Selection ────────────────────────────────────────────────────────────

  selectFromList(event) {
    const id = event.currentTarget.dataset.featureId
    this.select(id, { fly: true })
  }

  select(id, { fly = false } = {}) {
    const feature = this._byId.get(id)
    if (!feature) return

    const previousId = this._selectedId
    this._selectedId = id
    this._highlightRow(id)
    this._paintSelection()
    this._releaseKept(previousId)
    // A site's own detail page mounts this controller for its single pin but
    // ships no detail panel — the page IS the detail. Clicking that pin must
    // not blow up looking for targets that were never rendered.
    if (this.hasDetailTarget) this._fillDetail(feature)
    // Plan mode docks the card in its right column, rendered server-side
    // from the feature this names (prospecting--plan).
    this.dispatch("selected", { detail: { feature } })
    this._resetAccountSites(feature)

    if (fly && this._map && feature.lngLat) {
      // Zoom in only when the map is further out than street level; a rep who
      // has already zoomed into an estate does not want to be yanked back.
      const zoom = Math.max(this._map.getZoom(), 14)
      this._map.flyTo({ center: feature.lngLat, zoom, speed: 1.2 })
    }
  }

  closeDetail() {
    const previousId = this._selectedId
    this._selectedId = null
    this._resetAccountSites(null)
    if (this.hasDetailTarget) this.detailTarget.hidden = true
    this._highlightRow(null)
    this._paintSelection()
    this._releaseKept(previousId)
  }

  // A linked saved site kept on the map only because the rep was inspecting
  // it (drawnFeatures' keepId) gives way to its account's pin once the rep
  // moves on.
  _releaseKept(previousId) {
    if (!previousId || previousId === this._selectedId) return

    const feature = this._byId.get(previousId)
    if (feature?.kind === "saved" && feature.account_id) this._applyBoxes()
  }

  // The feature the card is showing, if any: plan mode docks it in its right
  // column when a route starts.
  selectedFeature() {
    return this._selectedId ? this._byId.get(this._selectedId) || null : null
  }

  // The board pin for a saved site, when the page has one: a route stop's row
  // selects its pin through this.
  featureIdForSite(siteId) {
    for (const feature of this._byId.values()) {
      if (feature.site_id === siteId) return feature.id
    }
    return null
  }

  // A card's "Add to Favourites" or "Create qualified lead" just answered, in place:
  // the stream replaced the page's marker with what the pin now is. The page
  // renders the marker empty, so only an answer carries a site.
  siteMarkerTargetConnected(element) {
    // Before connect(): connect() plays every marker again once it has built
    // the index (a throw here would escape Stimulus and stop every controller
    // after this one connecting).
    if (!this._byId) return

    const { featureId, siteId, sitePath, tone, accountId, convertFormPath } =
      element.dataset
    if (!featureId || !siteId) return

    this._markSite(featureId, {
      siteId,
      sitePath,
      tone,
      accountId,
      convertFormPath,
    })
    // The card stays open on the pin it just saved or converted, now offering
    // the next step. Re-filled only when it is still that pin's card.
    if (this._selectedId === featureId && this.hasDetailTarget) {
      this._fillDetail(this._byId.get(featureId), { answered: true })
    }
  }

  // A candidate just saved, or a saved site just converted: its pin and its
  // list dot say so straight away rather than on the next search. Every value
  // was built server-side (the tone, the site page, the convert form's URL).
  _markSite(
    id,
    { siteId, sitePath, tone = "saved", accountId, convertFormPath },
  ) {
    const feature = this._byId.get(id)
    if (!feature) return

    // The box that drew a board pin keeps governing it: after a reload it is
    // a saved site in the saved tone again (MapFeature.from_site).
    if (feature.context && !feature.context_tone) {
      feature.context_tone = feature.tone
    }
    Object.assign(feature, {
      kind: "saved",
      tone,
      site_id: siteId,
      site_path: sitePath,
      account_id: accountId || null,
      convert_form_path: convertFormPath || null,
      candidate: null,
    })
    const colour = this.pinColorsValue[tone] || FALLBACK_COLOR
    this.rowTargets
      .filter((row) => row.dataset.featureId === id)
      .forEach((row) => {
        const dot = row.querySelector("[data-tone-dot]")
        // A hollow dot is a row with no pin; it stays hollow.
        if (dot && dot.style.backgroundColor) dot.style.backgroundColor = colour
      })
    // The pins, and the legend that names their tones.
    this._applyBoxes()
  }

  // ── The map boxes ────────────────────────────────────────────────────────

  // "Also show on the map", applied where it is clicked. The page already
  // holds every context pin the rep may see (the server flags each one
  // `context` and never sends a kind the rep may not read), so a box only
  // changes which are drawn: the pins, the rows, the legend and the lines over
  // the list. No request and no submit — a box never re-runs the search, so it
  // can never cost a directory or a metered AWS call. The URL follows, so a
  // reload or a bookmark shows the same map. Plan mode's route is a layer of
  // its own and no box reaches it.
  //
  // Except on a page no search has filled yet: it holds the board and no
  // account at all, so a Leads, Prospects or Customers tick submits the form
  // instead, which posts the viewport and the tick. The form as it stands is
  // what is searched, so an empty one asks for the CRM's Sites in view and
  // nothing else — no directory call.
  toggleTone(event) {
    const box = event.currentTarget
    const shown = new Set(this.shownTonesValue)
    if (box.checked) {
      shown.add(box.dataset.tone)
    } else {
      shown.delete(box.dataset.tone)
    }
    this.shownTonesValue = [...shown]

    this._chooseBox(box.name)
    if (
      box.checked &&
      box.dataset.tone !== "saved" &&
      !this.contextLoadedValue &&
      this.hasSearchFormTarget
    ) {
      this.searchFormTarget.requestSubmit()
      return
    }
    this._applyBoxes()
    this._followBoxesInUrl(box.name, box.checked)
  }

  // ── Filters that search as they change ─────────────────────────────────

  // The Ultimate Parent Group and the industry re-run the search on their
  // own, as the boxes redraw the map on their own (the user's call,
  // 2026-10-13): picking or clearing either is one change, since both are
  // pickers (the industry stopped being typed on 2026-10-14). The "No
  // appointment in" period joined them on 2026-10-28 (CONEK-9122); the "With
  // an open quote" box still waits for Search, by the user's call. The values
  // the page was rendered with are remembered, so a field put back to them
  // searches nothing: an industry search is a metered directory call.
  searchFormTargetConnected(form) {
    this._rememberSearched(form)
  }

  // Any submission — the button, Enter, or #refine — is now the searched state.
  submitted(event) {
    this._rememberSearched(event.currentTarget)
  }

  _rememberSearched(form) {
    this._searched = Object.fromEntries(
      REFINING_FIELDS.map((name) => [
        name,
        (form.elements[name]?.value ?? "").trim(),
      ]),
    )
  }

  refine(event) {
    const field = event.target
    if (!REFINING_FIELDS.includes(field?.name)) return

    if (field.value.trim() === (this._searched?.[field.name] ?? "")) return

    this.searchFormTarget.requestSubmit()
  }

  _drawn(features) {
    return drawnFeatures(features, new Set(this.shownTonesValue), {
      keepId: this._selectedId,
    })
  }

  // The box is the rep's own answer now, `1` or `0`, and outlives the next
  // search's defaults (SearchesController#chosen_toggles).
  _chooseBox(param) {
    if (!this.hasBoxesChosenTarget) return

    const chosen = this.boxesChosenTarget.value.split(",").filter(Boolean)
    if (!chosen.includes(param)) chosen.push(param)
    this.boxesChosenTarget.value = chosen.join(",")
    this.boxesChosenTarget.disabled = false
  }

  // Replaces the entry rather than pushing one: a box is not a page the rep
  // should step back through. It goes through Turbo's history, not the native
  // one: Turbo caches this page under the URL it last rendered, and Back looks
  // the snapshot up under the entry's URL. A native replaceState left the two
  // apart, so Back missed the cache and re-ran the search — a directory call
  // and up to 50 geocodes for ticking a box. Native only where no Turbo runs.
  _followBoxesInUrl(param, ticked) {
    const url = new URL(window.location.href)
    url.searchParams.set(param, ticked ? "1" : "0")
    if (this.hasBoxesChosenTarget) {
      url.searchParams.set(
        this.boxesChosenTarget.name,
        this.boxesChosenTarget.value,
      )
    }
    if (this.hasBoxesDefaultTarget) {
      url.searchParams.set(
        this.boxesDefaultTarget.name,
        this.boxesDefaultTarget.value,
      )
    }
    const turbo = window.Turbo?.navigator
    if (turbo?.history && turbo.view) {
      // Same restoration id, so the entry keeps its scroll position.
      turbo.history.replace(url, turbo.history.restorationIdentifier)
      turbo.view.lastRenderedLocation = url
    } else {
      window.history.replaceState(window.history.state, "", url)
    }
  }

  _applyBoxes() {
    const drawn = this._drawn([...this._byId.values()])
    const drawnIds = new Set(drawn.map((f) => f.id))

    const source = this._map?.getSource(SOURCE_ID)
    if (source && this._features) {
      source.setData(this._featureCollection(this._features))
    }
    if (this._selectedId && !drawnIds.has(this._selectedId)) this.closeDetail()

    this._applyToList(drawnIds)
    this._applyToLegend(drawn)
    this._applyToNotes(drawn)
  }

  _applyToList(drawnIds) {
    const shownGroups = new Set()
    this.listItemTargets.forEach((item) => {
      item.hidden = !drawnIds.has(item.dataset.featureId)
      if (!item.hidden) shownGroups.add(item.dataset.group)
    })
    this.groupHeadingTargets.forEach((heading) => {
      heading.hidden = !shownGroups.has(heading.dataset.group)
    })
    if (this.hasEmptyStateTarget)
      this.emptyStateTarget.hidden = drawnIds.size > 0
  }

  // The tones on the map right now: a row with no coordinate draws no pin.
  _applyToLegend(drawn) {
    const tones = new Set(drawn.filter((f) => f.lngLat).map((f) => f.tone))
    this.legendToneTargets.forEach((item) => {
      item.hidden = !tones.has(item.dataset.tone)
    })
    if (this.hasLegendListTarget)
      this.legendListTarget.hidden = tones.size === 0
    // An attribute, not `hidden`: in plan mode the card still carries the
    // route's days (see the searches view).
    if (this.hasLegendTarget) {
      this.legendTarget.toggleAttribute("data-legend-empty", tones.size === 0)
    }
  }

  // The same rules the view applies on first paint (searches/index).
  _applyToNotes(drawn) {
    const locale = document.documentElement.lang
    const forms = (element, key) => JSON.parse(element.dataset[key] || "{}")
    const drawnKnown = drawn.some((f) => f.kind === "known")

    if (this.hasCountTarget) {
      const results = drawn.filter(
        (f) => !f.context || f.kind === "known",
      ).length
      this.countTarget.textContent =
        results > 0
          ? pluralise(forms(this.countTarget, "resultsForms"), results, locale)
          : pluralise(
              forms(this.countTarget, "savedForms"),
              drawn.length - results,
              locale,
            )
    }
    if (this.hasUnmappedNoteTarget) {
      const unmapped = drawn.filter((f) => !f.lngLat).length
      this.unmappedNoteTarget.hidden = unmapped === 0
      this.unmappedNoteTarget.textContent =
        unmapped > 0
          ? pluralise(forms(this.unmappedNoteTarget, "forms"), unmapped, locale)
          : ""
    }
    if (this.hasCappedNoteTarget) {
      const note = this.cappedNoteTarget
      const bit = (cap) => note.dataset[cap] === "true"
      note.hidden = !(
        bit("directoriesTruncated") ||
        (bit("knownTruncated") && drawnKnown) ||
        (bit("savedTruncated") && this.shownTonesValue.includes("saved"))
      )
      const text = note.querySelector("[data-note-text]")
      if (text)
        text.textContent = pluralise(forms(note, "forms"), drawn.length, locale)
    }
    if (this.hasKnownTruncatedNoteTarget) {
      this.knownTruncatedNoteTarget.hidden = !drawnKnown
    }
  }

  // ── The route layer ───────────────────────────────────────────────────────

  // Plan mode's route, as the plan preview hands it over (Prospecting::RouteMap):
  // `lines`, one per route day, each a list of segments between two stops with
  // the leg's road shape or `road: null`; `stops`, a numbered pin each; and
  // `selected`, the "kind:id" of the stop the card shows. Null takes the route
  // away. Given before the map has loaded, it is drawn once it has.
  setRoute(route) {
    this._route = route || null
    this._drawRoute()
  }

  _drawRoute() {
    if (!this._map || !this._loaded) return

    this._addRouteLayer()
    this._map.getSource(ROUTE_SOURCE_ID)?.setData(this._routeLines())
    this._renderStopPins()
  }

  // A basemap swap wiped the lines with every other added layer. Not before
  // `load`, which draws the route itself, over the sites it has just added.
  _restoreRouteLayer() {
    if (!this._loaded) return

    this._addRouteLayer()
  }

  // Guarded on the source, so it is safe from every draw and every swap.
  // Under the sites' pins when they are there: the line runs between
  // the pins, never over them.
  _addRouteLayer() {
    if (!this._route || this._map.getSource(ROUTE_SOURCE_ID)) return

    this._map.addSource(ROUTE_SOURCE_ID, {
      type: "geojson",
      data: this._routeLines(),
    })
    const below = this._map.getLayer(LAYER_ID) ? LAYER_ID : undefined
    this._map.addLayer(
      {
        id: ROUTE_CASING_ID,
        type: "line",
        source: ROUTE_SOURCE_ID,
        layout: ROUTE_LINE_LAYOUT,
        paint: ROUTE_CASING_PAINT,
      },
      below,
    )
    this._map.addLayer(
      {
        id: ROUTE_LINE_ID,
        type: "line",
        source: ROUTE_SOURCE_ID,
        layout: ROUTE_LINE_LAYOUT,
        paint: ROUTE_LINE_PAINT,
      },
      below,
    )
  }

  // ── The selected account's other sites ───────────────────────────────────

  // "See all locations": the lines are drawn only when the rep asks for them,
  // and a second press takes them away. Selecting another pin or closing the
  // card clears them (#_resetAccountSites).
  async toggleAccountSites() {
    const feature = this._byId.get(this._selectedId)
    if (!feature) return
    if (this.hasAccountSitesNoneTarget)
      this.accountSitesNoneTarget.hidden = true

    if (this._accountSitesShown) {
      this._accountSitesShown = false
      this._setAccountSitesButton(false)
      this._showAccountSites(null)
      return
    }

    this._accountSitesShown = true
    this._setAccountSitesButton(true)
    const answer = await this._showAccountSites(feature)
    // Dropped: the rep moved on or pressed again while it was asked.
    if (answer === null || !this._accountSitesShown) return
    if (answer === "drawn") return

    this._accountSitesShown = false
    this._setAccountSitesButton(false)
    if (answer === "none" && this.hasAccountSitesNoneTarget) {
      this.accountSitesNoneTarget.hidden = false
    }
  }

  // A new selection starts with no lines and the button up. The button is
  // offered only for a feature carrying an account, and only when the page
  // was handed where to ask (rendered with contact.read).
  _resetAccountSites(feature) {
    this._accountSitesShown = false
    this._showAccountSites(null)
    this._setAccountSitesButton(false)
    if (this.hasAccountSitesNoneTarget)
      this.accountSitesNoneTarget.hidden = true
    if (this.hasAccountSitesRowTarget) {
      this.accountSitesRowTarget.hidden = !(
        feature?.account_id && this.accountSitesUrlValue
      )
    }
  }

  // Ui::ButtonComponent's toggle contract: flip data-active, never a class.
  _setAccountSitesButton(active) {
    if (!this.hasAccountSitesButtonTarget) return
    this.accountSitesButtonTarget.dataset.active = active ? "true" : "false"
    this.accountSitesButtonTarget.setAttribute("aria-pressed", String(active))
  }

  // Asks where else the account is, and draws a line from its main location
  // to each of its other sites. No feature, or one with no account, clears
  // the lines. Answers "drawn", "none" (nothing else to draw), "failed", or
  // null for an answer that landed after the rep moved on, which is dropped.
  async _showAccountSites(feature) {
    const request = (this._accountSitesRequest =
      (this._accountSitesRequest || 0) + 1)
    this._accountSites = null
    this._accountSitePopup?.remove()
    this._accountSitePopup = null
    this._drawAccountSites()
    if (!feature || !feature.account_id || !this.accountSitesUrlValue) {
      return null
    }

    const url = this.accountSitesUrlValue.replace(
      ACCOUNT_ID_PLACEHOLDER,
      encodeURIComponent(feature.account_id),
    )
    const stale = () => request !== this._accountSitesRequest
    let body
    try {
      const response = await get(url, { responseKind: "json" })
      if (!response.ok) return stale() ? null : "failed"
      body = await response.json
    } catch {
      // the pin and its card stand without the lines
      return stale() ? null : "failed"
    }
    if (stale()) return null

    const sites = (body?.sites || [])
      .map((site) => ({ ...site, lngLat: this._point(site.lng_lat) }))
      .filter((site) => site.lngLat)
    const main = sites.find((site) => site.main)
    const origin = main?.lngLat || feature.lngLat
    if (!origin) return "none"

    const others = sites.filter(
      (site) =>
        !site.main &&
        (site.lngLat[0] !== origin[0] || site.lngLat[1] !== origin[1]),
    )
    if (others.length === 0) return "none"

    this._accountSites = {
      origin,
      colour: this._toneColour(feature.tone),
      sites: others,
    }
    this._drawAccountSites()
    this._fitAccountSites()
    return "drawn"
  }

  _drawAccountSites() {
    if (!this._map || !this._loaded) return

    this._addAccountSitesLayer()
    this._map
      .getSource(ACCOUNT_SITES_SOURCE_ID)
      ?.setData(this._accountSitesData())
    const colour = this._accountSites?.colour
    if (colour) {
      this._map.setPaintProperty(ACCOUNT_SITES_LINE_ID, "line-color", colour)
      this._map.setPaintProperty(ACCOUNT_SITES_DOT_ID, "circle-color", colour)
    }
  }

  // Guarded on the source, so it is safe from every draw and every basemap
  // swap; under the pins, so a line never covers the account it reaches from.
  // Added only once there is something to draw: a map no account was
  // selected on carries no layer of it.
  _addAccountSitesLayer() {
    if (!this._map || !this._loaded || !this._accountSites) return
    if (this._map.getSource(ACCOUNT_SITES_SOURCE_ID)) return

    this._map.addSource(ACCOUNT_SITES_SOURCE_ID, {
      type: "geojson",
      data: this._accountSitesData(),
    })
    const below = this._map.getLayer(LAYER_ID) ? LAYER_ID : undefined
    const colour = this._accountSites?.colour || FALLBACK_COLOR
    this._map.addLayer(
      {
        id: ACCOUNT_SITES_LINE_ID,
        type: "line",
        source: ACCOUNT_SITES_SOURCE_ID,
        filter: ["==", ["geometry-type"], "LineString"],
        layout: { "line-cap": "round" },
        paint: {
          "line-color": colour,
          "line-width": 2,
          "line-dasharray": [2, 2],
          "line-opacity": 0.85,
        },
      },
      below,
    )
    this._map.addLayer(
      {
        id: ACCOUNT_SITES_DOT_ID,
        type: "circle",
        source: ACCOUNT_SITES_SOURCE_ID,
        filter: ["==", ["geometry-type"], "Point"],
        paint: {
          "circle-color": colour,
          "circle-radius": 5,
          "circle-stroke-color": "#ffffff",
          "circle-stroke-width": 2,
        },
      },
      below,
    )
    // Registered on the MAP, which survives a swap: once only.
    if (this._accountSitesHandlers) return
    this._accountSitesHandlers = true
    this._map.on("click", ACCOUNT_SITES_DOT_ID, (event) =>
      this._showAccountSitePopup(event),
    )
    this._map.on("mouseenter", ACCOUNT_SITES_DOT_ID, () => {
      this._map.getCanvas().style.cursor = "pointer"
    })
    this._map.on("mouseleave", ACCOUNT_SITES_DOT_ID, () => {
      this._map.getCanvas().style.cursor = ""
    })
  }

  _accountSitesData() {
    const drawn = this._accountSites
    if (!drawn) return { type: "FeatureCollection", features: [] }

    const lines = drawn.sites.map((site) => ({
      type: "Feature",
      geometry: {
        type: "LineString",
        coordinates: [drawn.origin, site.lngLat],
      },
      properties: {},
    }))
    const dots = drawn.sites.map((site) => ({
      type: "Feature",
      geometry: { type: "Point", coordinates: site.lngLat },
      properties: { name: site.name || "", address: site.address || "" },
    }))
    return { type: "FeatureCollection", features: [...lines, ...dots] }
  }

  // A site's name and address, as text: both are the CRM's, typed by people.
  _showAccountSitePopup(event) {
    const hit = event.features && event.features[0]
    if (!hit) return

    const content = document.createElement("div")
    content.className = "text-xs"
    const name = document.createElement("p")
    name.className = "font-medium"
    name.textContent = hit.properties.name
    const address = document.createElement("p")
    address.className = "text-muted-foreground"
    address.textContent = hit.properties.address
    content.append(name, address)

    this._accountSitePopup?.remove()
    this._accountSitePopup = new maplibregl.Popup({ closeButton: false })
      .setLngLat(hit.geometry.coordinates)
      .setDOMContent(content)
      .addTo(this._map)
  }

  // Brings every site into view when some fall outside it: the lines are the
  // answer to "where else is it?", and an answer off-screen reads as none.
  _fitAccountSites() {
    const drawn = this._accountSites
    if (!drawn || !this._map) return

    const points = [drawn.origin, ...drawn.sites.map((site) => site.lngLat)]
    const view = this._map.getBounds()
    const inView = points.every(
      ([lng, lat]) =>
        lat >= view.getSouth() &&
        lat <= view.getNorth() &&
        lng >= view.getWest() &&
        lng <= view.getEast(),
    )
    if (!inView) this.fitPoints(points)
  }

  _toneColour(tone) {
    return (this.pinColorsValue && this.pinColorsValue[tone]) || FALLBACK_COLOR
  }

  // Each day's segments end to end: a segment follows its road shape when
  // the day routed, and runs straight between its two stops when it did not,
  // so the order stays visible. A segment with no valid point to draw is
  // left out, never drawn to 0,0.
  _routeLines() {
    const features = ((this._route && this._route.lines) || []).flatMap(
      (line) => {
        const coordinates = []
        ;(line.segments || []).forEach((segment) => {
          const road = this._points(segment.road)
          const points =
            road && road.length >= 2
              ? road
              : this._points([segment.from, segment.to])
          if (!points) return
          points.forEach((point, index) => {
            const last = coordinates[coordinates.length - 1]
            const join =
              index === 0 &&
              last &&
              last[0] === point[0] &&
              last[1] === point[1]
            if (!join) coordinates.push(point)
          })
        })
        if (coordinates.length < 2) return []
        return [
          {
            type: "Feature",
            geometry: { type: "LineString", coordinates },
            properties: { colour: line.colour, day: line.day },
          },
        ]
      },
    )
    return { type: "FeatureCollection", features }
  }

  // One marker per stop, in its day's colour, carrying its number within the
  // day. Markers are DOM, so they need no glyphs from the basemap and survive
  // a style swap untouched; and they are never dimmed.
  _renderStopPins() {
    // A click on a pin answers with a fresh plan and so fresh pins: the
    // keyboard focus goes to the same stop's new pin, not back to the page.
    const focused = this._stopPins
      .map((marker) => marker.getElement().firstChild)
      .find((badge) => badge === document.activeElement)?.dataset.stop
    this._clearStopPins()
    const selected = this._route && this._route.selected
    ;((this._route && this._route.stops) || []).forEach((stop) => {
      const lngLat = this._point(stop.lng_lat)
      if (!lngLat) return

      const key = `${stop.place_kind}:${stop.place_id}`
      const isSelected = selected === key
      // MapLibre positions a marker by writing `transform` on the element it
      // is given, so the selection's scale goes on an inner badge.
      const element = document.createElement("div")
      const badge = document.createElement("button")
      badge.type = "button"
      // Text, never markup: a place's name comes from a third-party directory.
      badge.textContent = String(stop.number)
      badge.setAttribute("aria-label", `${stop.number} · ${stop.name}`)
      badge.dataset.stop = key
      badge.dataset.selected = String(isSelected)
      Object.assign(badge.style, {
        display: "block",
        width: "26px",
        height: "26px",
        borderRadius: "9999px",
        border: "2px solid #ffffff",
        background: stop.colour,
        color: "#ffffff",
        font: "600 12px/22px system-ui, sans-serif",
        textAlign: "center",
        boxShadow: "0 1px 4px rgba(0,0,0,.35)",
        cursor: "pointer",
        padding: "0",
        transition: "transform 120ms ease-out",
        transform: isSelected ? "scale(1.25)" : "",
        outline: isSelected ? "3px solid rgba(15,23,42,.55)" : "",
      })
      element.style.zIndex = isSelected ? "2" : ""
      badge.addEventListener("click", (event) => {
        // The map's own click would read it as a click on the canvas.
        event.stopPropagation()
        this.dispatch("stop-selected", {
          detail: { kind: stop.place_kind, id: stop.place_id },
        })
      })
      element.appendChild(badge)
      this._stopPins.push(
        new maplibregl.Marker({ element }).setLngLat(lngLat).addTo(this._map),
      )
      if (key === focused) badge.focus()
    })
  }

  _clearStopPins() {
    ;(this._stopPins || []).forEach((marker) => marker.remove())
    this._stopPins = []
  }

  _highlightRow(id) {
    this.rowTargets.forEach((row) => {
      const isSelected = row.dataset.featureId === id
      row.dataset.selected = String(isSelected)
      if (isSelected) row.scrollIntoView({ block: "nearest" })
    })
  }

  // Drives the map's own highlight from the layer's own size rather
  // than a second layer, so the selected pin cannot drift out of sync with the
  // list.
  //
  // Runs on every select/close AND after every basemap swap, so the size it
  // writes is the WHOLE story from _sizeExpression() and _sortKeyExpression().
  _paintSelection() {
    if (!this._map || !this._map.getLayer(LAYER_ID)) return

    this._map.setLayoutProperty(LAYER_ID, "icon-size", this._sizeExpression())
    this._map.setLayoutProperty(
      LAYER_ID,
      "symbol-sort-key",
      this._sortKeyExpression(),
    )
  }

  // THE size story: the pin the rep is inspecting, and everything else. One
  // builder because it is told in two places — the layer's own layout when it
  // is (re)built, and every selection repaint — and two hand-kept encodings of
  // it would drift apart.
  //
  // A dimmed pin is drawn at the resting size, at full opacity.
  _sizeExpression() {
    const selected = ["==", ["get", "id"], this._selectedId ?? ""]
    return ["case", selected, SELECT_SIZE, BASE_SIZE]
  }

  // The draw order, told the same two places as the size: each pin's own
  // sortKey (_featureCollection), with the selected pin above every one.
  _sortKeyExpression() {
    const selected = ["==", ["get", "id"], this._selectedId ?? ""]
    return ["case", selected, SELECTED_SORT_KEY, ["get", "sortKey"]]
  }

  // ── Detail card ──────────────────────────────────────────────────────────

  // `answered`: re-filled because this pin's own save or conversion just
  // answered (siteMarkerTargetConnected), rather than opened.
  _fillDetail(feature, { answered = false } = {}) {
    this.detailTarget.hidden = false
    // textContent throughout: names, addresses and sectors come from a
    // third-party directory and are not ours to trust as markup.
    this.detailNameTarget.textContent = feature.name || ""
    // Hidden rather than emptied: an empty line still takes its margin, and the
    // server already suppresses an address that only repeats the name.
    this.detailAddressTarget.textContent = feature.address || ""
    this.detailAddressTarget.hidden = !feature.address

    // The line that says a promoted candidate IS an account we already have:
    // "Already in CONEK as <account>". Built server-side — where the
    // account's name sits in the sentence is a locale's decision — and set as
    // text, since the name is data, never markup. Absent for every other
    // kind of pin: a plain account location is already named by the card.
    this.detailKnownAccountTarget.textContent =
      feature.already_in_conek_label || ""
    this.detailKnownAccountTarget.hidden = !feature.already_in_conek_label

    // Only an account we already have HAS a funnel state. The badge is the only
    // place it is ever shown — the pin's colour says which kind of pin this is,
    // not where the account sits in the funnel. Label built server-side; the
    // badge's own styling is the design system's, not this controller's.
    this._field(
      this.detailStatusRowTarget,
      this.detailStatusTarget,
      feature.status_label,
    )

    // A field with nothing in it is hidden rather than filled with an em dash.
    // Six dashes in a grid tell the rep nothing and bury the two facts that are
    // actually there. Money arrives pre-formatted from the server, which is the
    // only place that knows the currency (CONEK-4332).
    this._field(
      this.detailEmployeesRowTarget,
      this.detailEmployeesTarget,
      this._number(feature.employees),
    )
    this._field(
      this.detailRevenueRowTarget,
      this.detailRevenueTarget,
      feature.revenue,
    )
    this._field(
      this.detailSectorRowTarget,
      this.detailSectorTarget,
      feature.sector,
    )
    this.detailSourceTarget.textContent =
      feature.source_label || feature.source || ""

    const website = HTTP_URL.test(feature.website || "")
      ? feature.website
      : null
    this.detailWebsiteRowTarget.hidden = !website
    if (website) {
      this.detailWebsiteTarget.href = website
      this.detailWebsiteTarget.textContent = feature.website_label || website
    }

    // What the business directory knows about the business. These targets
    // ship on the search page's card only; a page without them skips them.
    if (this.hasDetailPhoneTarget) this._fillDirectory(feature)

    // Said out loud rather than left to be inferred from a pin that never moved.
    this.detailNoLocationTarget.hidden = Boolean(feature.lngLat)

    // A saved pin opens its site page, an account already in CONEK opens ITS
    // site page (found or created server-side), and only an unsaved candidate
    // offers the save form. Never two of the three — a "Save" on something
    // already ours is how a duplicate of it gets created.
    const known = feature.kind === "known"
    const saved = Boolean(feature.site_id)
    // No form for a rep who may not save a site (the read-only role).
    if (this.hasSaveFormTarget) this.saveFormTarget.hidden = saved || known
    this.openLinkTarget.hidden = !saved
    // The page renders ONE of the two account controls: the site form for a rep
    // who may create a site, the company link for one who may not.
    if (this.hasAccountSiteFormTarget) {
      this.accountSiteFormTarget.hidden = !known
      if (known) this.accountSiteFieldTarget.value = feature.account_id || ""
    }
    if (this.hasAccountLinkTarget) {
      this.accountLinkTarget.hidden = !known
      if (known) {
        // Built server-side, carrying the ACCOUNT's own branch: rebuilding a
        // space-scoped URL here would land the rep on "Site not found in
        // selected branch" the moment the account lives in another branch.
        this.accountLinkTarget.href = feature.account_path
      }
    }
    if (saved) {
      // Built server-side by the route helper — reconstructing a space-scoped
      // URL from window.location here would break the moment the route changes.
      this.openLinkTarget.href = feature.site_path
    } else if (feature.candidate) {
      this.candidateFieldTargets.forEach((input) => {
        const value = feature.candidate[input.dataset.field]
        input.value = value === null || value === undefined ? "" : value
      })
    }

    // The save answers in place (SearchesController#create, card mode), so
    // the form says which pin it saved, and a refusal shown for one pin never
    // stays on the next.
    if (this.hasSaveFeatureIdTarget) this.saveFeatureIdTarget.value = feature.id
    if (this.hasSaveErrorTarget) this.saveErrorTarget.replaceChildren()
    // A conversion's answer lands in the frame (its confirmation) beside the
    // marker that brought the card here: that one stays.
    if (!answered || feature.convert_form_path) this._resetConvert(feature)
  }

  // Cancel on the card's convert form: the card goes back to offering
  // "Create qualified lead".
  resetConvert() {
    const feature = this.selectedFeature()
    if (feature) this._resetConvert(feature)
  }

  // The convert frame emptied of any form or confirmation, holding the card's
  // own "Create qualified lead" when the server built a form for this pin —
  // it builds one only for a saved site not yet linked, with every gate open
  // (BaseController#card_convert_form_path). The link's frame fetch puts the
  // form here.
  _resetConvert(feature) {
    if (!this.hasConvertFrameTarget) return

    const frame = this.convertFrameTarget
    frame.removeAttribute("src")
    frame.removeAttribute("complete")
    frame.replaceChildren()
    if (!feature.convert_form_path || !this.hasConvertTemplateTarget) return

    frame.append(this.convertTemplateTarget.content.cloneNode(true))
    this.convertLinkTarget.href = feature.convert_form_path
  }

  // Phone, email, parent company and executives, from the directory. Every
  // value is set as text; the only hrefs are the ones checked above, and a
  // value with no valid href stays on the card as plain text.
  _fillDirectory(feature) {
    this._link(
      this.detailPhoneRowTarget,
      this.detailPhoneTarget,
      feature.phone,
      TEL_URL.test(feature.phone_href || "") ? feature.phone_href : null,
    )
    this._link(
      this.detailEmailRowTarget,
      this.detailEmailTarget,
      feature.email,
      MAILTO_URL.test(feature.email_href || "") ? feature.email_href : null,
    )

    // The directory's parent of this business, labelled "Parent company" in
    // the markup — never our Ultimate Parent Group.
    this.detailParentRowTarget.hidden = !feature.parent_company_name
    this.detailParentTarget.textContent = feature.parent_company_name || ""
    this.detailParentCountryTarget.textContent =
      feature.parent_company_country || ""
    this.detailParentCountryTarget.hidden = !feature.parent_company_country

    // People the DIRECTORY names, not our contacts: the first three, cloned
    // from a template so their styling stays in the markup, and the server's
    // "+N more" for the rest.
    const executives = (feature.executives || []).slice(0, CARD_EXECUTIVES)
    this.detailExecutivesRowTarget.hidden = executives.length === 0
    this.detailExecutivesTarget.replaceChildren(
      ...executives.map((executive) => {
        const item =
          this.executiveTemplateTarget.content.firstElementChild.cloneNode(true)
        item.querySelector("[data-executive-name]").textContent =
          executive.name || ""
        item.querySelector("[data-executive-title]").textContent =
          executive.title || ""
        return item
      }),
    )
    this.detailExecutivesMoreTarget.textContent =
      feature.executives_more_label || ""
    this.detailExecutivesMoreTarget.hidden = !feature.executives_more_label
  }

  _link(row, anchor, text, href) {
    row.hidden = !text
    anchor.textContent = text || ""
    if (href) anchor.setAttribute("href", href)
    else anchor.removeAttribute("href")
  }

  _field(row, valueEl, value) {
    const present = value !== null && value !== undefined && value !== ""
    row.hidden = !present
    if (present) valueEl.textContent = value
  }

  _number(value) {
    if (value === null || value === undefined || value === "") return null
    // The PAGE's language, the one the rest of the card is in — not the
    // browser's, which may differ. `|| undefined` so a page with no `lang`
    // falls back to the browser default rather than throwing on "".
    return Number(value).toLocaleString(
      document.documentElement.lang || undefined,
    )
  }

  // ── Basemap ──────────────────────────────────────────────────────────────

  // Swaps the whole style descriptor between the labelled street map and the
  // aerial imagery. Nothing else on the page changes: the viewport, the
  // selection and the search bounds all survive, because only the basemap is
  // being replaced under them.
  toggleBasemap(event) {
    const mode = event.currentTarget.dataset.basemap
    if (!this._map || mode === this._basemap) return

    const url =
      mode === "satellite" ? this.satelliteStyleUrlValue : this.styleUrlValue
    // No descriptor for that mode in this environment — leave the map on the
    // style it is already showing rather than tearing it down for a blank one.
    if (!url) return

    this._basemap = mode
    this.basemapButtonTargets.forEach((button) => {
      const active = button.dataset.basemap === mode
      // A single data attribute, per Ui::ButtonComponent's toggle contract —
      // no design-system class names in here.
      button.dataset.active = String(active)
      button.setAttribute("aria-pressed", String(active))
    })

    // diff: false — the street and aerial descriptors are unrelated documents,
    // and maplibre's differ spends its time proving that before falling back to
    // a full reload anyway.
    this._map.setStyle(url, { diff: false })
  }

  // Re-adds our pins, and their images, on top of a freshly swapped style.
  // Guarded on the layer rather than on a flag so it is safe to call from
  // every style.load.
  _restoreSitesLayer() {
    if (!this._features || this._features.length === 0) return
    if (this._map.getLayer(LAYER_ID)) return

    this._addSitesLayer(this._features)
    // The swap resets the layer's properties to its own defaults, so the
    // selected pin would silently shrink back to the size of every other one.
    this._paintSelection()
  }

  // ── Map plumbing ─────────────────────────────────────────────────────────

  // Coerce to a number, but treat null / undefined / "" as invalid rather than
  // letting Number() turn them into 0 — a half-null coordinate would otherwise
  // survive as a pin at [0, 0] and drag the viewport into the Atlantic.
  _toCoord(value) {
    if (value === null || value === undefined || value === "") return NaN
    return Number(value)
  }

  // A [lng, lat] pair as numbers, or null when either is missing.
  _point(pair) {
    const lng = this._toCoord((pair || [])[0])
    const lat = this._toCoord((pair || [])[1])
    return Number.isFinite(lng) && Number.isFinite(lat) ? [lng, lat] : null
  }

  // Every point of a list, or null when the list is none or any point in it
  // is not one.
  _points(list) {
    if (!Array.isArray(list)) return null
    const points = list.map((pair) => this._point(pair))
    return points.every(Boolean) ? points : null
  }

  // Attaches a usable `lngLat` where one exists and leaves it null otherwise —
  // it never DROPS a feature, because a row without a coordinate still belongs
  // in the list.
  _withCoords(features) {
    return (features || []).map((feature) => ({
      ...feature,
      lngLat: this._point(feature.lng_lat),
    }))
  }

  // The box the server sent for the place, or null when there is none. Either
  // source will do: the place the rep picked from the suggestions, and failing
  // that the one the server parsed out of the typed query — the map goes where
  // the search went, and a directory-only search whose rows are all unmapped
  // still lands on the named area.
  _placeBox() {
    return (
      this._box(this.placeBoundsValue) ||
      this._box(this.hasLocationBoxValue ? this.locationBoxValue : null)
    )
  }

  // The viewport the search was submitted from, or null on a first visit.
  _viewportBox() {
    return this._box(this.viewportBoundsValue)
  }

  // A box's centre as [lng, lat] (GeoJSON order, never [lat, lng]), or null for
  // no box. Only ever fed a box `_box` just validated and unwrapped, so the
  // naive midpoint is safe — a box across the antimeridian arrives with its
  // east past 180 rather than as west > east.
  _boxCentre(box) {
    if (!box) return null
    return [(box.west + box.east) / 2, (box.south + box.north) / 2]
  }

  // A box is all four edges or nothing. A half-filled one would fit the map to
  // the Atlantic, the geographic form of the CONEK-4332 zero-as-fact bug. The
  // server spells a box across the antimeridian west > east (it is what
  // `_writeBounds` posts), so it is unwrapped here for MapLibre to centre and
  // fit it the short way round.
  _box(value) {
    const box = value || {}
    const edges = [box.south, box.west, box.north, box.east].map((edge) =>
      this._toCoord(edge),
    )
    return edges.every((edge) => Number.isFinite(edge))
      ? unwrapBox({
          south: edges[0],
          west: edges[1],
          north: edges[2],
          east: edges[3],
        })
      : null
  }

  // A saved route opened into plan mode: the map frames its stops, `points`
  // as [lng, lat]. Opened before the map has loaded, the frame waits for it
  // and wins over the restored viewport: the rep asked for this route.
  fitPoints(points) {
    if (!points || points.length === 0) return

    const lngs = points.map(([lng]) => lng)
    const lats = points.map(([, lat]) => lat)
    const box = {
      south: Math.min(...lats),
      west: Math.min(...lngs),
      north: Math.max(...lats),
      east: Math.max(...lngs),
    }
    if (this._map && this._loaded) {
      this._fitBox(box, { maxZoom: 13 })
    } else {
      this._pendingFit = box
    }
  }

  // `maxZoom` is a ceiling for a box we CHOSE for the rep — a pinpoint place or
  // a single pin would otherwise land them on a rooftop. Restoring their own
  // viewport passes null for it: clamping there would zoom a rep who was
  // working at street level back out on every search, which is the bug this
  // whole camera path exists to fix.
  _fitBox(box, { padding = null, maxZoom = 14, animate = true } = {}) {
    // resize() first so fitBounds computes against the container's real size —
    // the box can still be laying out when the map inits.
    this._map.resize()
    // A container with no width or height (hidden, collapsed, not laid out yet)
    // leaves MapLibre nothing to fit into: with padding 0 it divides its way to
    // a NaN centre and throws "Invalid LngLat object: (NaN, NaN)". The camera
    // was already seeded at this box's centre, so skipping the fit is safe.
    if (!this._hasSize()) return
    this._map.fitBounds(
      [
        [box.west, box.south],
        [box.east, box.north],
      ],
      {
        padding: padding ?? this._fitPadding(),
        // Omitted, never `undefined`: maplibre merges the options over its own
        // defaults, so an explicit `maxZoom: undefined` REPLACES the map's max
        // zoom, the camera maths turns to NaN and fitBounds throws "Invalid
        // LngLat (NaN, NaN)" — which blanked the map after every search.
        ...(maxZoom === null ? {} : { maxZoom }),
        animate,
      },
    )
  }

  _renderFeatures(features, { fit = true } = {}) {
    if (features.length === 0) return

    // Kept so a basemap swap can rebuild the layer from the same features.
    this._features = features
    this._addSitesLayer(features)

    this._map.on("click", LAYER_ID, (event) => {
      const hit = event.features && event.features[0]
      if (!hit) return
      // No flyTo: the rep clicked a pin they can already see, and recentring
      // under their cursor is disorienting.
      this.select(hit.properties.id, { fly: false })
    })
    this._map.on("mouseenter", LAYER_ID, () => {
      this._map.getCanvas().style.cursor = "pointer"
    })
    this._map.on("mouseleave", LAYER_ID, () => {
      this._map.getCanvas().style.cursor = ""
    })

    const drawn = this._drawn(features)
    if (!fit || drawn.length === 0) return

    const bounds = drawn.reduce(
      (acc, feature) => acc.extend(feature.lngLat),
      new maplibregl.LngLatBounds(drawn[0].lngLat, drawn[0].lngLat),
    )
    // resize() first so fitBounds computes against the container's real size —
    // the box can still be laying out when the map inits.
    this._map.resize()
    // A container with no width or height (hidden, collapsed, not laid out yet)
    // leaves MapLibre nothing to fit into: with padding 0 it divides its way to
    // a NaN centre and throws "Invalid LngLat object: (NaN, NaN)". The camera
    // was already seeded at this box's centre, so skipping the fit is safe.
    if (!this._hasSize()) return
    this._map.fitBounds(bounds, { padding: this._fitPadding(), maxZoom: 14 })
  }

  // Source + layer only. The click/hover handlers and the initial fitBounds
  // live in _renderFeatures because they are registered against the MAP, not
  // the style, and survive a basemap swap — re-running them on every swap would
  // stack duplicate listeners and yank the viewport back to the first fit.
  _addSitesLayer(features) {
    this._addPinImages()
    this._map.addSource(SOURCE_ID, {
      type: "geojson",
      data: this._featureCollection(features),
    })

    // The size and draw order are the shared stories from _sizeExpression()
    // and _sortKeyExpression(), baked into the LAYER's own layout rather than
    // applied by a setLayoutProperty afterwards:
    // a basemap swap tears every layer down and _restoreSitesLayer rebuilds
    // from here, so a selection standing at swap time has to survive without
    // anything else remembering to re-apply it.
    this._map.addLayer({
      id: LAYER_ID,
      type: "symbol",
      source: SOURCE_ID,
      layout: {
        "icon-image": ["get", "icon"],
        // The tip is the place, so the marker hangs from it.
        "icon-anchor": "bottom",
        "icon-size": this._sizeExpression(),
        // Every pin is drawn, however crowded: a symbol layer would otherwise
        // drop the ones that collide, and a hidden pin reads as "nothing here".
        "icon-allow-overlap": true,
        "icon-ignore-placement": true,
        "symbol-sort-key": this._sortKeyExpression(),
      },
    })
  }

  // One image per tone in the palette, plus the fallback: each a pin in the
  // body colour around a dot in its tone. setStyle() drops images with the
  // layers, so this runs on every (re)build, and skips what the map still has.
  _addPinImages() {
    const body = parseHex(this.pinBodyColorValue) || FALLBACK_BODY
    // Drawn at twice the screen's density, so a selected pin, scaled up
    // 1.625 times, is still drawn from real pixels.
    const pixelRatio = Math.max(1, Math.ceil(window.devicePixelRatio || 1)) * 2
    const images = [
      ...Object.keys(this.pinColorsValue).map((tone) => [
        this._pinImageId(tone),
        this.pinColorsValue[tone],
      ]),
      [PIN_IMAGE_PREFIX, FALLBACK_COLOR],
    ]
    images.forEach(([id, colour]) => {
      if (this._map.hasImage(id)) return

      const toneRgb = parseHex(colour) || parseHex(FALLBACK_COLOR)
      this._map.addImage(id, pinImage({ body, tone: toneRgb, pixelRatio }), {
        pixelRatio,
      })
    })
  }

  // Keyed on the TONE, never the kind: `known` is three colours.
  _pinImageId(tone) {
    return this.pinColorsValue[tone]
      ? `${PIN_IMAGE_PREFIX}-${tone}`
      : PIN_IMAGE_PREFIX
  }

  // Only the features drawn under the boxes: the rest stay in `features`,
  // one tick away.
  _featureCollection(features) {
    return {
      type: "FeatureCollection",
      features: this._drawn(features).map((feature) => ({
        type: "Feature",
        geometry: { type: "Point", coordinates: feature.lngLat },
        properties: {
          id: feature.id,
          icon: this._pinImageId(feature.tone),
          // A dimmed context pin sinks below every result, so a new site is
          // never hidden under a lead or a saved site on the same spot.
          sortKey:
            (KIND_SORT_KEYS[feature.kind] || CANDIDATE_SORT_KEY) +
            (feature.dimmed ? 0 : 10),
          dimmed: Boolean(feature.dimmed),
        },
      })),
    }
  }

  // Keeps the floating panels clear of the pins they describe — WITHOUT ever
  // asking for more padding than the map has room for.
  //
  // The panels only overlay the map from `md` up; below that they sit above and
  // below it and the map needs no side gutter at all. And in a narrow window the
  // fixed 384 + 352 came to more than the container's own width, at which point
  // fitBounds has no viewport left to fit into and quietly parks the pins
  // outside it — the saved sites were on the map, just never on screen.
  _fitPadding() {
    const { width, height } = this._map.getContainer().getBoundingClientRect()
    // Tailwind's `md` breakpoint, the one the panels' own classes switch on.
    const overlaid = width >= 768
    // Half the box minus a margin, so the two opposite paddings can never meet.
    const maxX = Math.max(0, width / 2 - 40)
    const maxY = Math.max(0, height / 2 - 40)

    return {
      top: Math.min(48, maxY),
      bottom: Math.min(48, maxY),
      left: Math.min(overlaid ? 384 : 24, maxX),
      right: Math.min(overlaid ? 352 : 24, maxX),
    }
  }

  // Writes the viewport into the search form's hidden fields. All four edges or
  // none: the server rejects a half-specified box outright (a missing edge
  // coerced to 0 spans the Atlantic), so never write a partial set. And every
  // longitude in [-180, 180] (see ./map_bounds): the server drops a box outside
  // that range, and getBounds() reports one the moment the rep pans past the
  // antimeridian or zooms out past a single world.
  _writeBounds() {
    if (
      !this.hasSouthTarget ||
      !this.hasWestTarget ||
      !this.hasNorthTarget ||
      !this.hasEastTarget
    ) {
      return
    }

    // A zero-size map reports a zero-area box; writing it would make the next
    // search restore a single point and land the rep on a rooftop.
    if (!this._hasSize()) return

    const bounds = this._map.getBounds()
    const box = normaliseViewport({
      south: bounds.getSouth(),
      west: bounds.getWest(),
      north: bounds.getNorth(),
      east: bounds.getEast(),
    })
    this.southTarget.value = box.south
    this.westTarget.value = box.west
    this.northTarget.value = box.north
    this.eastTarget.value = box.east
  }

  _hasSize() {
    const container = this._map.getContainer()
    return container.clientWidth > 0 && container.clientHeight > 0
  }
}
